/**
 * The small field components of the link form (LinkForm.tsx), split out when the screening-link
 * variant (Oct 24 2026) took the form past the file-length limit.
 */
import { Input } from '@finn-auto/design-system/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { cn } from '../../../../shared';

export function MeetingTypeCard({
  active,
  icon: Icon,
  title,
  description,
  onClick,
}: {
  active: boolean;
  icon: React.ElementType;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors',
        active ? 'border-[#0072EA] bg-[#DBEBFC]' : 'border-gray-200 bg-white hover:border-gray-400'
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', active ? 'text-[#0C447C]' : 'text-gray-400')} />
      <span>
        <span className="block text-sm font-semibold text-gray-900">{title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-gray-600">{description}</span>
      </span>
    </button>
  );
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-700">
        {label} {required ? <span className="text-red-600">*</span> : null}
      </span>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-xl" />
      {hint ? <span className="mt-1 block text-xs text-gray-500">{hint}</span> : null}
    </label>
  );
}

export function NumberSelect({
  label,
  value,
  options,
  unit,
  onChange,
  required = false,
  hint,
}: {
  label: string;
  value: number;
  options: number[];
  unit: string;
  onChange: (v: number) => void;
  required?: boolean;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-700">
        {label} {required ? <span className="text-red-600">*</span> : null}
      </span>
      <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
        <SelectTrigger className="w-full rounded-xl">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={String(o)}>
              {o === 0 ? 'None' : `${o} ${unit}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint ? <span className="mt-1 block text-xs text-gray-500">{hint}</span> : null}
    </label>
  );
}
